import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import * as d3 from 'd3';
import d3Cloud from 'd3-cloud';
import _ from 'lodash';

const COLOR_SMALL = '#0073b4';
const COLOR_LARGE = '#9ed7ff';
const COLOR_HIGHLIGHTED = '#7DC855';
const COLOR_DISABLED = '#ccc';

const PADDING = 10;
const PADDING_BETWEEN_WORDS = 3;
const SCALE_FACTOR_FONT = 75;

@Component({
  selector: 'app-d3-word-cloud',
  templateUrl: './d3-word-cloud.component.html',
  styleUrls: ['./d3-word-cloud.component.scss'],
})
export class D3WordCloudComponent implements OnInit {
  @ViewChild('divParent', { static: true }) divParent: ElementRef;
  @Input() maxWords = 70;
  @Output() clicked = new EventEmitter();
  @Input() words: string[];

  private _filteredWords: string[] = [];
  private _highlightedWords: string[] = [];
  private _disabledWords: string[] = [];
  private width: number;
  private height: number;
  private dataPointsByWord = {};
  private svg: d3.Selection<SVGGElement, unknown, null, undefined>;
  private initialized = false;

  private minFontSize = 15;
  private maxFontSize = 30;
  private filteredWordsForCloud: any[];

  @Input() set filteredWords(words) {
    let recompute = false;
    if (words.length !== this.getFilteredWords().length) {
      recompute = true;
    }
    this.setFilteredWords(words);
    if (recompute && words && this.filteredWordsForCloud) {
      this.initializeAll();
    }
  }

  @Input() set disabledWords(words) {
    this._disabledWords = words;
    if (this.svg) {
      this.updateCloud();
    }
  }

  @Input() set highlightedWords(words) {
    this._highlightedWords = words || [];
    let reComputRequired = false;
    if (words && this.filteredWordsForCloud) {
      for (const word of words) {
        if (this.filteredWordsForCloud.indexOf(word) < 0) {
          reComputRequired = true;
        }
      }
    }

    if (reComputRequired) {
      this.initializeAll();
    } else if (this.svg) {
      this.updateCloud();
    }
  }

  setFilteredWords(words) {
    this._filteredWords = words;
  }

  getFilteredWords() {
    return this._filteredWords;
  }

  getWidth() {
    return this.width;
  }

  getHeight() {
    return this.height;
  }

  computeFontSize() {
    this.minFontSize = Math.min(
      50,
      Math.max(2, this.getWidth() / SCALE_FACTOR_FONT),
    );
    this.maxFontSize = this.minFontSize * 2.5;
  }

  initWordCloud() {
    // show always filtered words first (+highlighted), fill up with biggest words from unfiltered collection (grey items)

    this.dataPointsByWord = {};

    const filteredGroupedWords = this.getFilteredGroupedWords();
    const unfilteredGroupedWords = this.getUnfilteredGroupedWords();

    this.filteredWordsForCloud = _.reverse(
      _.sortBy(
        _.keys(filteredGroupedWords),
        word => filteredGroupedWords[word].length,
      ),
    ).slice(0, this.maxWords);
    this.filteredWordsForCloud = _.uniq(
      this._highlightedWords.concat(this.filteredWordsForCloud),
    );
    if (this.filteredWordsForCloud.length < this.maxWords) {
      const unfilteredWordsForCloud = _.reverse(
        _.sortBy(
          _.keys(unfilteredGroupedWords),
          word => unfilteredGroupedWords[word].length,
        ),
      ).slice(0, this.maxWords - this.filteredWordsForCloud.length);
      this.filteredWordsForCloud = this.filteredWordsForCloud.concat(
        unfilteredWordsForCloud,
      );
    }

    const getNumberOfOccurence = word => {
      if (word in filteredGroupedWords) {
        return filteredGroupedWords[word].length;
      } else {
        return unfilteredGroupedWords[word].length;
      }
    };

    const maxCount = _.max(
      this.filteredWordsForCloud.map(getNumberOfOccurence),
    );
    const minCount = _.min(
      this.filteredWordsForCloud.map(getNumberOfOccurence),
    );

    const sizeScale = d3
      .scaleLinear()
      .domain([minCount - 1, maxCount])
      .range([this.minFontSize, this.maxFontSize]);

    const wordDataPoints = this.filteredWordsForCloud.map(word => {
      return {
        word: word,
        text: word.split('_').join(' ').toUpperCase(),
        size: sizeScale(getNumberOfOccurence(word)),
      };
    });

    d3Cloud()
      .size([this.getWidth(), this.getHeight()])
      .words(wordDataPoints)
      .padding(PADDING_BETWEEN_WORDS)
      .rotate(() => {
        return 0;
      })
      .font('Arial')
      .fontSize(d => {
        return d.size;
      })
      .random(() => 1)
      .on('end', dataPoints => this.onDataPointsComputed(dataPoints))
      .start();
  }

  onDataPointsComputed(dataPoints) {
    for (const dataPoint of dataPoints) {
      this.dataPointsByWord[dataPoint['word']] = dataPoint;
    }
  }

  draw(wordDataPoints) {
    const x = d3.extent(wordDataPoints, d => d['x']);
    const y = d3.extent(wordDataPoints, d => d['y']);

    const minX = Math.max(parseFloat(x[0]), -this.getWidth() / 2);
    const maxX = Math.min(parseFloat(x[1]), this.getWidth() / 2);

    const minY = Math.max(parseFloat(y[0]), -this.getHeight() / 2);
    const maxY = Math.min(parseFloat(y[1]), this.getHeight() / 2);

    const xScale = d3
      .scaleLinear()
      .domain([minX - this.maxFontSize * 1.5, maxX + this.maxFontSize * 1.5])
      .range([-this.getWidth() / 2 + PADDING, this.getWidth() / 2 - PADDING]);

    const yScale = d3
      .scaleLinear()
      .domain([minY - this.maxFontSize * 1.5, maxY + this.maxFontSize * 1.5])
      .range([-this.getHeight() / 2 + PADDING, this.getHeight() / 2 - PADDING]);

    const cloud = this.svg.selectAll('g text').data(wordDataPoints, d => {
      return d['text'];
    });

    // Entering wordDataPoints
    cloud
      .enter()
      .append('text')
      .style('font-family', 'Arial')
      .style('font-weight', 'bold')
      .attr('text-anchor', 'middle')
      .attr('font-size', 1)
      .text(d => {
        return d['text'];
      })

      .on('mouseover', (e, d: any) => {
        if (!d.disabled) {
          d3.select(e.target)
            .style('cursor', 'pointer')
            .transition()
            .duration(100)
            .style('font-size', d['scaledSize'] * 1.1 + 'px');
        }
      })
      .on('mouseout', (e, d) => {
        d3.select(e.target)
          .style('cursor', 'default')
          .transition()
          .duration(100)
          .style('font-size', d['scaledSize'] + 'px');
      })
      .on('click', d => {
        if (!d.disabled || d.highlighted) {
          this.clicked.emit(d['word']);
        }
      });

    // Entering and existing wordDataPoints
    cloud
      .style('fill', d => {
        return d['color'];
      })
      .style('font-size', d => {
        return d['scaledSize'] + 'px';
      })
      .attr('transform', d => {
        return (
          'translate(' +
          [xScale(d['x']), yScale(d['y'])] +
          ')rotate(' +
          d['rotate'] +
          ')'
        );
      })
      .style('fill-opacity', 1);

    // Exiting wordDataPoints
    cloud.exit().remove();
  }

  initSvg(selector) {
    // Construct the word cloud's SVG element
    this.svg = d3
      .select(selector)
      .append('svg')
      .attr('id', 'd3Svg')
      .attr('class', 'd3-svg')
      .style('user-select', 'none')
      .attr('width', this.getWidth())
      .attr('height', this.getHeight())
      .append('g')
      .attr(
        'transform',
        `translate(${this.getWidth() / 2},${this.getHeight() / 2})`,
      );
  }

  ngOnInit() {
    this.initialized = true;
    this.width = this.divParent.nativeElement.offsetWidth;
    this.height = this.divParent.nativeElement.offsetHeight;
    this.computeFontSize();
    this.initializeAll();
  }

  getFilteredGroupedWords() {
    return _.groupBy(this.getFilteredWords(), _.identity);
  }

  getUnfilteredGroupedWords() {
    return _.groupBy(
      _.difference(this.getWords(), this.getFilteredWords()),
      _.identity,
    );
  }

  //tags that were "filtered away" (shown in grey)
  getAllGroupedWords() {
    return _.groupBy(this.getWords(), _.identity);
  }

  getWords() {
    return this.words;
  }

  updateCloud() {
    const allGroupedWords = this.getAllGroupedWords();
    const filteredGroupedWords = this.getFilteredGroupedWords();
    const filteredWords = _.reverse(
      _.sortBy(_.keys(allGroupedWords), word => allGroupedWords[word].length),
    ).slice(0, this.maxWords);

    const getNumberOfOccurence = word => {
      if (word in filteredGroupedWords) {
        return filteredGroupedWords[word].length;
      } else {
        return allGroupedWords[word].length;
      }
    };

    const maxCount = _.max(filteredWords.map(getNumberOfOccurence));
    const minCount = _.min(filteredWords.map(getNumberOfOccurence));

    const colorScale = d3.scaleLinear(
      [minCount - 1, maxCount],
      [COLOR_SMALL, COLOR_LARGE],
    );
    const sizeScale = d3.scaleLinear(
      [minCount - 1, maxCount],
      [this.minFontSize, this.maxFontSize],
    );

    for (const word of this.words) {
      if (word in this.dataPointsByWord) {
        const highlighted =
          this._highlightedWords && this._highlightedWords.indexOf(word) >= 0;
        const disabled =
          this._disabledWords && this._disabledWords.indexOf(word) >= 0;

        let color;
        if (highlighted) {
          color = COLOR_HIGHLIGHTED;
        } else if (disabled) {
          color = COLOR_DISABLED;
        } else {
          color = colorScale(getNumberOfOccurence(word));
        }
        this.dataPointsByWord[word].color = color;
        this.dataPointsByWord[word].disabled = disabled;
        this.dataPointsByWord[word].highlighted = highlighted;

        // rescale words from the filtered collection (e.g. tag may occur 100 times in all ideas, but only 2 times in filtered ideas)
        if (word in filteredGroupedWords) {
          this.dataPointsByWord[word].scaledSize = sizeScale(
            getNumberOfOccurence(word),
          );
        } else {
          this.dataPointsByWord[word].scaledSize = sizeScale(
            getNumberOfOccurence(word),
          );
        }
      } else {
        continue;
      }
    }

    this.draw(_.values(this.dataPointsByWord));
  }

  removeSvg() {
    d3.select('.d3-word-cloud').selectAll('svg').remove();
  }

  initializeAll() {
    this.removeSvg();
    this.initSvg('.d3-word-cloud');
    this.computeFontSize();
    this.initWordCloud();
    this.updateCloud();
  }

  // recompute wordcloud when component size changed
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.removeSvg();

    const boundingClientRect =
      this.divParent.nativeElement.getBoundingClientRect();
    this.initSvg('.d3-word-cloud');

    this.width = boundingClientRect.width;
    this.height = boundingClientRect.height;

    this.computeFontSize();
    this.initializeAll();
  }
}
